import React from 'react';

interface TraceRow {
    id: number;
    created_at: string;
    item_type?: string;
    crop_type: string;
    quantity_kg: number;
    direction: 'incoming' | 'outgoing' | 'internal';
    reason: string;
    source_type?: string | null;
    destination_type?: string | null;
    farmer_name?: string | null;
    cohort_name?: string | null;
    facility_name?: string | null;
    quality_grade?: string | null;
}

interface Props {
    rows: TraceRow[];
    loading?: boolean;
}

const TraceabilityPanel: React.FC<Props> = ({ rows, loading }) => {
    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="animate-pulse space-y-3">
                    <div className="h-5 w-52 bg-slate-200 rounded" />
                    <div className="h-10 bg-slate-100 rounded" />
                    <div className="h-10 bg-slate-100 rounded" />
                    <div className="h-10 bg-slate-100 rounded" />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-900">Traceability Stream</h3>
                <p className="text-sm text-slate-600">Farm/cohort-linked flow from source to destination</p>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-white border-b border-slate-100">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Flow</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Farmer / Cohort</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Qty</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Facility</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.slice(0, 12).map((r) => (
                            <tr key={r.id} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm text-slate-700">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-slate-900 capitalize">
                                        {r.item_type || 'produce'} / {r.crop_type}
                                    </div>
                                    <div className="text-xs text-slate-500 capitalize">
                                        {r.reason}{r.quality_grade ? ` • Grade ${r.quality_grade}` : ''}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-700 capitalize">
                                    {(r.source_type || 'n/a')} {'->'} {(r.destination_type || 'n/a')}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-sm text-slate-800">{r.farmer_name || '-'}</div>
                                    <div className="text-xs text-slate-500">{r.cohort_name || '-'}</div>
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{Number(r.quantity_kg || 0).toFixed(1)} kg</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{r.facility_name || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rows.length === 0 && (
                <div className="px-6 py-10 text-center text-slate-500 text-sm">
                    No traceability records yet.
                </div>
            )}
        </div>
    );
};

export default TraceabilityPanel;
